/**
 * The members grid's clinic columns: Clinics, Clinic roles, Disciplines and
 * Employee type. Each edit saves the member's whole clinic setup in one
 * request; where a value differs between clinics the cell says so and opens
 * the per-clinic editor. Cells are plain functions the grid calls directly, so
 * none of them may call hooks.
 */

import type { ColumnDef } from '@tanstack/react-table';
import { MultiSelectCell, ReadCell, SelectCell } from '../../spreadsheet';
import type { DisciplineType } from '../../../services/api/models/DisciplineType';
import type { EmployeeType } from '../../../services/api/models/EmployeeType';
import { DISCIPLINE_ORDER } from '../../../constants/disciplines';
import { EMPLOYEE_TYPE_OPTIONS } from '../../../constants/employeeTypes';
import { isAgencyMember } from '../memberStatus';
import type { OrgMemberResponse } from '../types';
import {
  CLINIC_ROLE_OPTIONS,
  type ClinicRoleIds,
  type ClinicRoles,
  type ClinicSetupEntry,
  DISCIPLINE_OPTIONS,
  type Employment,
  employmentText,
  needsOtherText,
  newClinicEntry,
  rolesText,
  setupEntries,
  uniformDisciplines,
  uniformEmployment,
  uniformRoles,
  withDisciplines,
  withEmployment,
  withRoles,
} from './clinicSetup';

const VARIES = 'Varies by clinic';

export type ClinicColumnsContext = {
  /** `clinician-mgr:access`, which the save requires. */
  canEdit: boolean;
  /** Clinic roles and Disciplines: EMR agencies with the clinic-info flag. */
  showClinicRoles: boolean;
  /**
   * The clinics the operator administers: the only ones a save touches. Null
   * until loaded, or when the operator cannot edit: cells then show every
   * clinic.
   */
  boundIds: ReadonlySet<string> | null;
  clinicOptions: { value: string; label: string }[];
  /** How many clinics the agency has, to say "All clinics". */
  clinicCount: number;
  clinicNamesById?: ReadonlyMap<string, string>;
  clinicName: (id: string) => string;
  /** The three clinic roles' ids; null until the catalogue has loaded. */
  roleIds: ClinicRoleIds | null;
  /** The role a flag is held through when not the grid's own; see clinicSetup. */
  heldThrough: (
    entry: ClinicSetupEntry,
    key: keyof ClinicRoles
  ) => string | null;
  /** Saves, asking first when the edit takes clinics or every role away. */
  saveSetup: (member: OrgMemberResponse, entries: ClinicSetupEntry[]) => void;
  /** The per-clinic editor, optionally with an employee type to fill in. */
  openSetup: (member: OrgMemberResponse, employment?: Employment) => void;
};

/** The clinics a member can access, by the page's resolved names. */
export function clinicNames(
  member: OrgMemberResponse,
  clinicNamesById?: ReadonlyMap<string, string>
): string[] {
  return (member.clinic_access ?? []).map(
    entry => clinicNamesById?.get(entry.clinic_id) ?? entry.clinic_name
  );
}

/**
 * What one save from a dropdown changes: the items ticked and unticked since
 * it opened. Each goes to every clinic and nothing else does, so a value that
 * differs by clinic is never flattened by ticking something else.
 */
function toggled(before: readonly string[], after: readonly string[]) {
  return {
    added: after.filter(v => !before.includes(v)),
    removed: before.filter(v => !after.includes(v)),
  };
}

/** "At Tempe, Mesa": where an item held at only some clinics is held. */
function heldAt(
  ctx: ClinicColumnsContext,
  entries: ClinicSetupEntry[],
  holds: (e: ClinicSetupEntry) => boolean
): string | undefined {
  const at = entries.filter(holds);
  return at.length && at.length < entries.length
    ? `At ${at.map(e => ctx.clinicName(e.clinic_id)).join(', ')}`
    : undefined;
}

/** The last row of each clinic dropdown: the per-clinic editor. */
function variesFooter(ctx: ClinicColumnsContext, member: OrgMemberResponse) {
  return { label: `${VARIES}…`, onSelect: () => ctx.openSetup(member) };
}

function noClinicCell(title: string) {
  return (
    <ReadCell title={title} muted>
      —
    </ReadCell>
  );
}

export function clinicColumns(
  ctx: ClinicColumnsContext
): ColumnDef<OrgMemberResponse>[] {
  const entriesOf = (member: OrgMemberResponse) =>
    setupEntries(member, ctx.boundIds);
  const canEdit = (member: OrgMemberResponse) =>
    ctx.canEdit && isAgencyMember(member);
  return [
    {
      id: 'clinics',
      header: 'Clinics',
      meta: {
        clipboard: {
          copy: m => clinicNames(m, ctx.clinicNamesById).join(', '),
        },
      },
      enableSorting: false,
      size: 180,
      cell: ({ row }) => {
        const member = row.original;
        const names = clinicNames(member, ctx.clinicNamesById);
        const entries = entriesOf(member);
        // One clinic by name, several as a count, every one as All: every
        // clinic the operator manages, and none outside them.
        const text =
          names.length === 0
            ? '—'
            : names.length === 1
              ? names[0]
              : entries.length >= ctx.clinicCount &&
                  entries.length === names.length
                ? 'All clinics'
                : `${names.length} clinics`;
        if (!canEdit(member)) {
          return (
            <ReadCell title={names.join('\n')} muted={!names.length}>
              {text}
            </ReadCell>
          );
        }
        return (
          <MultiSelectCell
            options={ctx.clinicOptions}
            value={entries.map(e => e.clinic_id)}
            text={names.length ? text : undefined}
            placeholder='—'
            ariaLabel={`Clinics for ${member.email}`}
            onChange={ids =>
              // A new clinic takes the setup their other clinics share.
              ctx.saveSetup(
                member,
                ids.map(
                  id =>
                    entries.find(e => e.clinic_id === id) ??
                    newClinicEntry(
                      entries,
                      id,
                      // Without the role columns a new clinic grants access only.
                      ctx.showClinicRoles ? ctx.roleIds : null
                    )
                )
              )
            }
          />
        );
      },
    },
    ...(ctx.showClinicRoles
      ? ([
          {
            id: 'clinic_roles',
            header: 'Clinic roles',
            meta: {
              clipboard: {
                copy: m => {
                  const shared = uniformRoles(entriesOf(m));
                  return shared ? rolesText(shared) : VARIES;
                },
              },
            },
            enableSorting: false,
            size: 180,
            cell: ({ row }) => {
              const member = row.original;
              const entries = entriesOf(member);
              if (!member.roger_user_id || !entries.length) {
                return noClinicCell('Add a clinic first');
              }
              const shared = uniformRoles(entries);
              const { roleIds } = ctx;
              if (!canEdit(member) || !roleIds) {
                const text = shared ? rolesText(shared) : VARIES;
                return (
                  <ReadCell muted={!shared || !text}>{text || '—'}</ReadCell>
                );
              }
              // A tick adds or removes that role at every clinic. Differences clinic
              // by clinic live in the per-clinic editor behind its last row.
              // A role held through another role stays ticked; the list says why,
              // and where a role is held at only some clinics.
              const options = CLINIC_ROLE_OPTIONS.map(option => {
                const locked = entries
                  .map(e => {
                    const role = ctx.heldThrough(e, option.value);
                    return role && `${role} at ${ctx.clinicName(e.clinic_id)}`;
                  })
                  .filter((text): text is string => !!text);
                const hint = locked.length
                  ? locked.join(', ')
                  : heldAt(ctx, entries, e => e[option.value]);
                return hint ? { ...option, hint } : option;
              });
              // Ticked: held at every clinic.
              const ticked = CLINIC_ROLE_OPTIONS.filter(o =>
                entries.every(e => e[o.value])
              ).map(o => o.value);
              return (
                <MultiSelectCell
                  options={options}
                  value={ticked}
                  // Every clinic role at every clinic, not only those ticked.
                  clearAll={{
                    disabled: !entries.some(
                      e => e.clinician || e.caseManager || e.clinicalManager
                    ),
                    run: () =>
                      ctx.saveSetup(
                        member,
                        entries.map(e =>
                          withRoles(
                            e,
                            {
                              clinician: false,
                              caseManager: false,
                              clinicalManager: false,
                            },
                            roleIds
                          )
                        )
                      ),
                  }}
                  text={shared ? rolesText(shared) || undefined : VARIES}
                  placeholder='—'
                  ariaLabel={`Clinic roles for ${member.email}`}
                  footer={variesFooter(ctx, member)}
                  onChange={values => {
                    const { added, removed } = toggled(ticked, values);
                    ctx.saveSetup(
                      member,
                      entries.map(e => {
                        const roles: ClinicRoles = {
                          clinician: e.clinician,
                          caseManager: e.caseManager,
                          clinicalManager: e.clinicalManager,
                        };
                        for (const key of added)
                          roles[key as keyof ClinicRoles] = true;
                        for (const key of removed)
                          roles[key as keyof ClinicRoles] = false;
                        return withRoles(e, roles, roleIds);
                      })
                    );
                  }}
                />
              );
            },
          },
          {
            id: 'disciplines',
            header: 'Disciplines',
            meta: {
              clipboard: {
                copy: m => {
                  const shared = uniformDisciplines(entriesOf(m));
                  return shared === undefined
                    ? ''
                    : shared === null
                      ? VARIES
                      : shared.join(', ');
                },
              },
            },
            enableSorting: false,
            size: 150,
            cell: ({ row }) => {
              const member = row.original;
              const entries = entriesOf(member);
              const shared = uniformDisciplines(entries);
              // Only a clinician has disciplines: the visit types they can take.
              if (!member.roger_user_id || shared === undefined) {
                return noClinicCell('Only for clinicians');
              }
              const all = shared?.length === DISCIPLINE_ORDER.length;
              if (!canEdit(member)) {
                return (
                  <ReadCell muted={!shared?.length}>
                    {shared === null
                      ? VARIES
                      : all
                        ? 'All'
                        : shared.join(', ') || '—'}
                  </ReadCell>
                );
              }
              // A tick adds or removes that discipline at every clinic they are a
              // clinician at; the list says where one is held at only some.
              const clinicianAt = entries.filter(e => e.clinician);
              const options = DISCIPLINE_OPTIONS.map(option => {
                const hint = heldAt(ctx, clinicianAt, e =>
                  e.disciplines.includes(option.value as DisciplineType)
                );
                return hint ? { ...option, hint } : option;
              });
              // Ticked: held at every clinic they are a clinician at.
              const ticked = DISCIPLINE_ORDER.filter(d =>
                clinicianAt.every(e => e.disciplines.includes(d))
              );
              return (
                <MultiSelectCell
                  options={options}
                  value={ticked}
                  // Every discipline at every clinic, not only those ticked.
                  clearAll={{
                    disabled: !clinicianAt.some(e => e.disciplines.length),
                    run: () =>
                      ctx.saveSetup(
                        member,
                        entries.map(e =>
                          e.clinician && e.disciplines.length
                            ? withDisciplines(e, [])
                            : e
                        )
                      ),
                  }}
                  text={shared === null ? VARIES : all ? 'All' : undefined}
                  placeholder='—'
                  ariaLabel={`Disciplines for ${member.email}`}
                  footer={variesFooter(ctx, member)}
                  onChange={values => {
                    const { added, removed } = toggled(ticked, values);
                    ctx.saveSetup(
                      member,
                      entries.map(e => {
                        if (!e.clinician) return e;
                        const next = DISCIPLINE_ORDER.filter(
                          d =>
                            added.includes(d) ||
                            (e.disciplines.includes(d) && !removed.includes(d))
                        );
                        return next.length === e.disciplines.length &&
                          next.every(d => e.disciplines.includes(d))
                          ? e
                          : withDisciplines(e, next);
                      })
                    );
                  }}
                />
              );
            },
          },
        ] satisfies ColumnDef<OrgMemberResponse>[])
      : []),
    {
      id: 'employee_type',
      header: 'Employee type',
      meta: {
        clipboard: {
          copy: m => {
            const shared = uniformEmployment(entriesOf(m));
            return shared ? employmentText(shared) : VARIES;
          },
        },
      },
      enableSorting: false,
      size: 150,
      cell: ({ row }) => {
        const member = row.original;
        const entries = entriesOf(member);
        if (!member.roger_user_id || !entries.length) {
          return noClinicCell('Add a clinic first');
        }
        const shared = uniformEmployment(entries);
        if (!canEdit(member)) {
          const text = shared ? employmentText(shared) : VARIES;
          return (
            <ReadCell muted={!shared?.employee_type}>{text || '—'}</ReadCell>
          );
        }
        return (
          <SelectCell
            options={EMPLOYEE_TYPE_OPTIONS}
            value={shared?.employee_type ?? null}
            text={shared ? employmentText(shared) || undefined : VARIES}
            placeholder='—'
            ariaLabel={`Employee type for ${member.email}`}
            footer={variesFooter(ctx, member)}
            onChange={value => {
              const type = value as EmployeeType;
              // "Other" needs saying what: the per-clinic editor asks once,
              // and the text goes to every clinic, as any other pick does.
              if (needsOtherText(type)) {
                ctx.openSetup(
                  member,
                  shared
                    ? withEmployment(shared, type)
                    : { employee_type: type, employee_type_other: null }
                );
                return;
              }
              ctx.saveSetup(
                member,
                entries.map(e => withEmployment(e, type))
              );
            }}
          />
        );
      },
    },
  ];
}
